import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Table, Icon, Select, Button } from 'antd';
import SearchText from '../../Reports/common/SearchText';
import DateFilter from '../../Reports/common/DateFilter';

const { Option } = Select;

const BlockedByHostTable = ({ onDataChange, selectedSpaces }) => {
    const {
        blockedDataSource: allData,
        blockedLoading: loading,
        spaceList
    } = useSelector(state => state.hostReport);

    const [tableFilters, setTableFilters] = useState({});

    // Client-side filtering
    const filteredData = useMemo(() => {
        let data = [...(allData || [])];

        // Filter by global space selection (prop)
        if (selectedSpaces && selectedSpaces.length > 0) {
            const selectedNames = (spaceList || [])
                .filter(s => selectedSpaces.includes(s.id))
                .map(s => s.spaceName);
            data = data.filter(item => selectedNames.includes(item.spaceName));
        }

        // Filter by event start date range
        if (tableFilters.eventStartDate && tableFilters.eventStartDate.length >= 2) {
            const start = moment(tableFilters.eventStartDate[0]);
            const end = moment(tableFilters.eventStartDate[1]);
            data = data.filter(item => {
                if (!item.eventStartDate) return false;
                const d = moment(item.eventStartDate, 'YYYY-MM-DD');
                return d.isBetween(start, end, 'day', '[]');
            });
        }

        // Filter by userName (substring)
        if (tableFilters.userName && tableFilters.userName.length > 0 && tableFilters.userName[0]) {
            const val = tableFilters.userName[0].toLowerCase();
            data = data.filter(item => item.userName && item.userName.toLowerCase().includes(val));
        }

        // Filter by userEmail (substring)
        if (tableFilters.userEmail && tableFilters.userEmail.length > 0 && tableFilters.userEmail[0]) {
            const val = tableFilters.userEmail[0].toLowerCase();
            data = data.filter(item => item.userEmail && item.userEmail.toLowerCase().includes(val));
        }

        return data;
    }, [allData, tableFilters, spaceList, selectedSpaces]);

    useEffect(() => {
        if (onDataChange) {
            onDataChange(filteredData);
        }
    }, [filteredData, onDataChange]);

    const handleTableChange = (pag, filt, sort) => {
        setTableFilters(filt || {});
    };

    const handleSearchList = (setSelectedKeys, confirm, val) => {
        const values = Array.isArray(val) ? val : [val];
        setSelectedKeys(values);
        confirm();
    };

    const handleResetList = (setSelectedKeys, clearFilters) => {
        clearFilters(); 
    };

    const columns = [
        {
            title: 'Space Name',
            dataIndex: 'spaceName',
            key: 'spaceIds',
            width: 250,
        },
        {
            title: 'Title',
            dataIndex: 'title', 
            key: 'title',
            width: 150,
        },
        {
            title: 'Booking Date',
            dataIndex: 'bookingDate',
            key: 'bookingDate',
            width: 150,
            render: text => text || 'N/A',
        },
        {
            title: 'Start Time',
            dataIndex: 'eventStartTime',
            key: 'eventStartTime',
            width: 100,
        },
        {
            title: 'End Time',
            dataIndex: 'eventEndTime',
            key: 'eventEndTime',
            width: 100,
        },
        {
            title: 'Start Date',
            dataIndex: 'eventStartDate',
            key: 'eventStartDate',
            width: 120,
            filteredValue: tableFilters.eventStartDate || null,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <DateFilter
                     placeholder={['Start', 'End']}
                     onSearch={(dates) => {
                         handleSearchList(setSelectedKeys, confirm, dates, 'eventStartDate');
                     }}
                     onReset={() => handleResetList(setSelectedKeys, clearFilters)}
                />
            ),
            filterIcon: filtered => <Icon type="calendar" style={{ color: filtered ? '#FF0054' : undefined }} />,
        },
        {
            title: 'End Date',
            dataIndex: 'eventEndDate',
            key: 'eventEndDate',
            width: 120,
        },
        {
            title: 'User Name',
            dataIndex: 'userName',
            key: 'userName',
            width: 150,
            filteredValue: tableFilters.userName || null,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <SearchText
                  placeholder="Search User Name"
                  onSearch={(val) => handleSearchList(setSelectedKeys, confirm, val, 'guestName', 'booking-tracker')}
                  onReset={() => handleResetList(setSelectedKeys, clearFilters)}
                />
            ),
            filterIcon: filtered => <Icon type="search" style={{ color: filtered ? '#FF0054' : undefined }} />,
        },
        {
            title: 'User Email',
            dataIndex: 'userEmail',
            key: 'userEmail',
            width: 200,
            filteredValue: tableFilters.userEmail || null,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <SearchText
                  placeholder="Search Email"
                  onSearch={(val) => handleSearchList(setSelectedKeys, confirm, val, 'guestEmail', 'booking-tracker')}
                  onReset={() => handleResetList(setSelectedKeys, clearFilters)}
                />
            ),
            filterIcon: filtered => <Icon type="search" style={{ color: filtered ? '#FF0054' : undefined }} />,
        }
    ];

    return (
        <Table
            className="table-card" 
            columns={columns}
            dataSource={filteredData} 
            pagination={filteredData.length > 10 ? { pageSize: 10, showSizeChanger: false } : false}
            loading={{
                spinning: loading,
                indicator: <Icon type="loading" style={{ fontSize: 24, color: '#FF0054' }} spin />
            }}
            onChange={handleTableChange}
            scroll={{ x: 1200 }}
            rowKey="bookingId"
            locale={{ 
                emptyText: (
                    <div style={{ padding: '30px 0', textAlign: 'center' }}>
                         <Icon type="stop" style={{ fontSize: '48px', color: '#e0e0e0', marginBottom: '10px' }} />
                         <p style={{ color: '#999', fontSize: '15px', margin: 0 }}>No Blocked Reservations</p>
                    </div>
                ) 
            }}
        />
    );
};

export default BlockedByHostTable;
